import React, { useState } from "react";
import { Flag, Link2, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const KPI_LABEL = { likes: "الإعجابات", comments: "التعليقات", reposts: "المشاركات" };

function Bar({ pct }) {
  const color = pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-primary" : pct >= 30 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
      <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

export function KrProgress({ projectId, items, onUpdated }) {
  const [edit, setEdit] = useState(null);
  const [val, setVal] = useState("");
  if (!items?.length) return null;
  const submit = async (i) => {
    try {
      const { data } = await api.put(`/projects/${projectId}/goals/kr-actual`, { index: i, actual: Number(val) || 0 });
      onUpdated?.(data.key_results);
      setEdit(null);
      toast.success("تم تحديث النتيجة");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const overall = Math.round(items.reduce((s, k) => s + (k.pct || 0), 0) / items.length);
  return (
    <div className="glass rounded-2xl p-6 border border-border" data-testid="kr-progress">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-bold text-base md:text-lg flex items-center gap-2"><Flag className="w-5 h-5 text-accent" /> تقدم النتائج الرئيسية (OKR)</h2>
        <span className="text-sm font-bold text-primary" data-testid="kr-progress-overall">الإجمالي {overall}%</span>
      </div>
      <div className="space-y-4">
        {items.map((k) => (
          <div key={k.index} data-testid={`kr-progress-${k.index}`}>
            <div className="flex items-start justify-between gap-3 mb-1.5">
              <div className="text-sm leading-snug">
                <span className="font-bold">KR{k.index + 1}:</span> {k.label}
                {k.auto && <span className="inline-flex items-center gap-1 ms-2 text-xs text-muted-foreground"><Link2 className="w-3 h-3" /> تلقائي من {KPI_LABEL[k.linked_kpi]}</span>}
              </div>
              <div className="text-sm whitespace-nowrap font-bold" dir="ltr" data-testid={`kr-progress-pct-${k.index}`}>{k.pct}%</div>
            </div>
            <Bar pct={k.pct} />
            <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
              <span dir="ltr">{Number(k.actual).toLocaleString()} / {Number(k.target).toLocaleString()} {k.unit}</span>
              {!k.auto && (edit === k.index ? (
                <span className="flex items-center gap-1">
                  <Input type="number" dir="ltr" value={val} onChange={(e) => setVal(e.target.value)} className="h-7 w-24 bg-secondary border-border text-xs" data-testid={`kr-actual-input-${k.index}`} />
                  <Button size="sm" className="h-7 text-xs bg-primary text-white" onClick={() => submit(k.index)} data-testid={`kr-actual-save-${k.index}`}>حفظ</Button>
                </span>
              ) : (
                <button type="button" onClick={() => { setEdit(k.index); setVal(String(k.actual || "")); }} className="flex items-center gap-1 text-primary" data-testid={`kr-actual-edit-${k.index}`}><Pencil className="w-3 h-3" /> أدخل المتحقق</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
